import { FileText } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Sheet } from '@/components/ui/Sheet';
import { PdfPreview } from './PdfPreview';

/**
 * Файл с сервера: просмотр и сохранение.
 *
 * Один экран для итогов ассистента и материалов урока — путь у них одинаковый.
 * Файл забирается запросом и показывается здесь: переход по ссылке в WebView
 * заменяет страницу и выгружает приложение вместе с открытым уроком.
 *
 * Встроенный просмотрщик браузера не используется: в мобильном WebView он
 * уводит страницу и ломает кнопку «Назад», а браузеры на Chromium отказываются
 * рисовать PDF во фрейме с песочницей. PDF рисует само приложение, картинки —
 * обычный `img`. Для остальных форматов показывать нечем, там остаётся
 * сохранение.
 *
 * Сохранение — именно запасной путь: часть клиентов запрещает загрузку из
 * мини-приложения, и тогда просмотр остаётся единственным способом прочитать
 * файл.
 */
export interface PreviewedFile {
  /** Локальный адрес уже загруженного файла. */
  url: string;
  name: string;
  contentType?: string;
}

export function FilePreview({
  file,
  onClose,
  onSave,
}: {
  file: PreviewedFile | null;
  onClose: () => void;
  onSave: () => void;
}) {
  const type = file?.contentType ?? '';
  const name = file?.name ?? '';
  const isImage = type.startsWith('image/');
  // Тип приходит не всегда: у части файлов он пустой, и расширение надёжнее
  const isPdf = type.includes('pdf') || name.toLowerCase().endsWith('.pdf');

  return (
    <Sheet open={file !== null} onClose={onClose} title={file?.name}>
      {file ? (
        <>
          {isImage ? (
            <img
              src={file.url}
              alt={file.name}
              className="mb-3 max-h-[60vh] w-full rounded-control border border-line object-contain"
            />
          ) : isPdf ? (
            <PdfPreview url={file.url} />
          ) : (
            <div className="mb-3 flex items-center gap-2.5 rounded-control border border-line bg-surface-2 px-3 py-3">
              <FileText className="h-5 w-5 shrink-0 text-muted" aria-hidden />
              <div className="min-w-0">
                <p className="truncate text-[13px] font-medium">{file.name}</p>
                <p className="text-[11px] text-muted">Материал от ассистента</p>
              </div>
            </div>
          )}

          <Button variant="primary" block onClick={onSave}>
            Сохранить файл
          </Button>

          {isImage || isPdf ? null : (
            <p className="mt-2 text-[11.5px] leading-[1.4] text-muted">
              Файл откроется приложением для документов на вашем устройстве.
            </p>
          )}

          <Button variant="ghost" block className="mt-2" onClick={onClose}>
            Закрыть
          </Button>
        </>
      ) : null}
    </Sheet>
  );
}
